import React, { createRef, useEffect, useState } from "react";
import {
  DrawingFileTypeModel,
  LocaleModel,
  drawingApi,
} from "../api/drawingApi";
import { Col, Form } from "react-bootstrap";
import { Problem } from "../api/backendApi";
import { CloudPlus } from "react-bootstrap-icons";

interface PictureLinkEditProps {
  vehicleFileNumber: string;
  onChange: (pictureId: string) => void;
  onError: (error: Problem | string | undefined) => void;
}

function PictureLinkEdit({
  vehicleFileNumber,
  onChange,
  onError,
}: PictureLinkEditProps) {
  const [pictureIds, setPictureIds] = useState<string[]>();
  const [selectedPictureId, setSelectedPictureId] = useState<string>();
  const refFileInput = createRef<HTMLInputElement>();

  useEffect(() => {
    async function getDrawingPictures() {
      const result = await drawingApi.getDrawingFiles(
        vehicleFileNumber,
        DrawingFileTypeModel.Picture,
      );

      if (result.ok) {
        onError(undefined);
        const pictureIds = result.value.map((p) => p.name);
        setPictureIds(pictureIds);

        let newSelectedPictureId = "";

        if (selectedPictureId) {
          const selectedIndex = pictureIds.indexOf(selectedPictureId);

          if (selectedIndex > 0) newSelectedPictureId = selectedPictureId;
        }

        setSelectedPictureId(newSelectedPictureId);
        onChange(newSelectedPictureId);
      } else {
        onError(result.problem);
      }
    }

    getDrawingPictures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFileNumber]);

  const handleInputFileChanged = async (files: FileList | null) => {
    if (!files || files.length !== 1) return;

    const file = files[0];

    const result = await drawingApi.uploadDrawingFile(
      vehicleFileNumber,
      file,
      file.name,
      LocaleModel.English,
      DrawingFileTypeModel.Picture,
    );

    if (result.ok) {
      if (result.value !== file.name) {
        // same file already exists
        onError(`Picture already exists with the name ${result.value}.`);
        setSelectedPictureId(result.value);
        return;
      }

      onError(undefined);
      setSelectedPictureId(file.name);

      if (!pictureIds || pictureIds.indexOf(file.name) < 0) {
        let newPictureIds = [...(pictureIds ?? []), file.name];
        newPictureIds.sort();
        setPictureIds(newPictureIds);
        onChange(file.name);
      }
    } else {
      onError(result.problem);
    }
  };

  return (
    <>
      <Col>
        <Form.Select
          size="sm"
          value={selectedPictureId}
          disabled={!pictureIds}
          onChange={(c) => {
            setSelectedPictureId(c.target.value);
            onChange(c.target.value);
          }}
        >
          {!pictureIds && <option value={undefined}>loading...</option>}
          {pictureIds && pictureIds.length === 0 && (
            <option value="">no pictures available...</option>
          )}
          {pictureIds && pictureIds.length > 0 && (
            <>
              <option key="" value="">
                [select]
              </option>
              {pictureIds.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </>
          )}
        </Form.Select>
      </Col>
      <Col md="auto">
        <CloudPlus
          className="mx-1"
          role="button"
          size="1.5em"
          title="Upload new picture"
          onClick={() => refFileInput.current?.click()}
        />
        <input
          type="file"
          ref={refFileInput}
          style={{ display: "none" }}
          onChange={(e) => handleInputFileChanged(e.target.files)}
          accept=".jpg,.jpeg"
        ></input>
      </Col>
    </>
  );
}

export default PictureLinkEdit;
